import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from "react";
import { SignIn, SignUp, useAuth } from "@clerk/clerk-react";
import { BrowserRouter, Navigate, Routes, Route, Outlet, useSearchParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink } from "@trpc/client";
import { trpc } from "./lib/trpc";
import { AuthTokenProvider, useAuthToken } from "./lib/authToken";
import { API_URL } from "./lib/env";
import HomeNow from "./pages/HomeNow";
import Pricing from "./pages/Pricing";
import Download from "./pages/Download";
import SampleHub from "./pages/samples/SampleHub";
import SampleLanding from "./pages/samples/SampleLanding";
import DesktopAuth from "./pages/DesktopAuth";
import StaticPage, { STATIC_PAGE_ROUTES } from "./pages/StaticPage";
import Dashboard from "./pages/Dashboard";
import History from "./pages/History";
import Settings from "./pages/Settings";
import Session from "./pages/Session";
import Overlay from "./pages/Overlay";
import { AppShell } from "./components/layout/AppShell";
const queryClient = new QueryClient({
    defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});
function TRPCProvider({ children }) {
    const getAuthToken = useAuthToken();
    const trpcClient = useMemo(() => trpc.createClient({
        links: [
            httpBatchLink({
                url: `${API_URL}/trpc`,
                headers: async () => {
                    const token = await getAuthToken();
                    return token ? { authorization: `Bearer ${token}` } : {};
                },
            }),
        ],
    }), [getAuthToken]);
    return (_jsx(trpc.Provider, { client: trpcClient, queryClient: queryClient, children: _jsx(QueryClientProvider, { client: queryClient, children: children }) }));
}
function ClerkAuthBridge({ children }) {
    const { getToken } = useAuth();
    const getAuthToken = useCallback(() => getToken(), [getToken]);
    return _jsx(AuthTokenProvider, { getToken: getAuthToken, children: children });
}
function checkoutRedirect(params) {
    const interval = params.get("interval") === "yearly" ? "yearly" : "monthly";
    return `/pricing?checkout=professional&interval=${interval}`;
}
const clerkAppearance = {
    variables: {
        colorBackground: "#0a0a0c",
        colorInputBackground: "#111114",
        colorInputText: "#f8fafc",
        colorText: "#f8fafc",
        colorTextSecondary: "#94a3b8",
        colorPrimary: "#ffffff",
        colorDanger: "#f87171",
        borderRadius: "12px",
    },
    elements: {
        card: "bg-[#0a0a0c] shadow-none border border-white/10",
        headerTitle: "text-white",
        headerSubtitle: "text-white/60",
        socialButtonsBlockButton: "bg-white/5 border-white/10 text-white",
        formFieldLabel: "text-white/70",
        footerActionLink: "text-white",
    },
};
function AuthPage({ type }) {
    const [params] = useSearchParams();
    const search = params.toString();
    const oppositeAuthUrl = `${type === "sign-in" ? "/sign-up" : "/sign-in"}${search ? `?${search}` : ""}`;
    // Only send users to Professional checkout when they explicitly chose to buy
    // (checkout=professional, set by the Pricing page). Everyone else — new
    // sign-ups and normal sign-ins — lands on the download page for the free trial.
    const redirectUrl = useMemo(() => (params.get("checkout") === "professional" ? checkoutRedirect(params) : "/download"), [params]);
    return (_jsxs("main", { className: "relative min-h-screen overflow-hidden bg-[#04151A] text-[#E7F6F1] flex items-center justify-center px-6 py-16", children: [_jsxs("div", { "aria-hidden": true, className: "pointer-events-none absolute inset-0", children: [_jsx("div", { className: "absolute -top-40 left-1/2 h-[32rem] w-[32rem] -translate-x-1/2 rounded-full bg-indigo-500/18 blur-[140px]" }), _jsx("div", { className: "evyely-grain" })] }), _jsxs("div", { className: "relative w-full max-w-md", children: [_jsx("p", { className: "mb-6 text-center text-[11px] font-medium tracking-[0.2em] text-white/45 uppercase", children: "Evyely" }), type === "sign-in" ? (_jsx(SignIn, { routing: "path", path: "/sign-in", signUpUrl: oppositeAuthUrl, forceRedirectUrl: redirectUrl, signUpForceRedirectUrl: redirectUrl, fallbackRedirectUrl: redirectUrl, appearance: clerkAppearance })) : (_jsx(SignUp, { routing: "path", path: "/sign-up", signInUrl: oppositeAuthUrl, forceRedirectUrl: redirectUrl, signInForceRedirectUrl: redirectUrl, fallbackRedirectUrl: redirectUrl, appearance: clerkAppearance }))] })] }));
}
function PricingRoute({ authEnabled }) {
    if (!authEnabled)
        return _jsx(Pricing, { authStatus: "disabled" });
    return _jsx(AuthenticatedPricing, {});
}
function AuthenticatedPricing() {
    const { isLoaded, isSignedIn } = useAuth();
    const authStatus = !isLoaded ? "loading" : isSignedIn ? "signed-in" : "signed-out";
    return _jsx(Pricing, { authStatus: authStatus });
}
function GuardedApp({ authEnabled }) {
    if (!authEnabled)
        return _jsx(Outlet, {});
    return _jsx(SignedInGate, {});
}
function SignedInGate() {
    const { isLoaded, isSignedIn } = useAuth();
    if (!isLoaded) {
        return (_jsx("div", { className: "grid min-h-[50vh] place-items-center text-sm text-white/55", children: "Loading workspace\u2026" }));
    }
    if (!isSignedIn)
        return _jsx(Navigate, { to: "/sign-in", replace: true });
    return _jsx(Outlet, {});
}
function AppRoutes({ authEnabled }) {
    return (_jsx(BrowserRouter, { children: _jsxs(Routes, { children: [_jsx(Route, { path: "/", element: _jsx(HomeNow, {}) }), _jsx(Route, { path: "/samples", element: _jsx(SampleHub, {}) }), _jsx(Route, { path: "/samples/:id", element: _jsx(SampleLanding, {}) }), _jsx(Route, { path: "/sign-in/*", element: authEnabled ? _jsx(AuthPage, { type: "sign-in" }) : _jsx(Navigate, { to: "/pricing", replace: true }) }), _jsx(Route, { path: "/sign-up/*", element: authEnabled ? _jsx(AuthPage, { type: "sign-up" }) : _jsx(Navigate, { to: "/pricing", replace: true }) }), _jsx(Route, { path: "/pricing", element: _jsx(PricingRoute, { authEnabled: authEnabled }) }), _jsx(Route, { path: "/download", element: _jsx(Download, {}) }), STATIC_PAGE_ROUTES.map((pageId) => (_jsx(Route, { path: `/${pageId}`, element: _jsx(StaticPage, { pageId: pageId }) }, pageId))), _jsx(Route, { path: "/desktop-auth", element: authEnabled ? _jsx(DesktopAuth, {}) : _jsx(Navigate, { to: "/", replace: true }) }), _jsx(Route, { path: "/overlay", element: _jsx(Overlay, {}) }), _jsx(Route, { path: "/session", element: _jsx(Session, {}) }), _jsx(Route, { element: _jsx(AppShell, { authEnabled: authEnabled }), children: _jsxs(Route, { element: _jsx(GuardedApp, { authEnabled: authEnabled }), children: [_jsx(Route, { path: "/dashboard", element: _jsx(Dashboard, {}) }), _jsx(Route, { path: "/history", element: _jsx(History, {}) }), _jsx(Route, { path: "/settings", element: _jsx(Settings, {}) })] }) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/", replace: true }) })] }) }));
}
export default function App({ authEnabled = false }) {
    const content = (_jsx(TRPCProvider, { children: _jsx(AppRoutes, { authEnabled: authEnabled }) }));
    return (authEnabled ? (_jsx(ClerkAuthBridge, { children: content })) : (_jsx(AuthTokenProvider, { children: content })));
}
